import { useLayoutEffect, useRef } from 'react'

const duration = 250

/**
 * Slides rows from where they were to where a new order puts them (FLIP), so a voter can follow an option they
 * just re-sorted instead of losing it. Rows are the container's descendants marked `data-reorder-key`.
 *
 * Positions are `offsetTop`, which is layout-only: a slide still in flight does not skew the next measurement.
 * Every commit records them, but only an order change animates, so a player opening mid-list does not.
 */
export const useReorderAnimation = <T extends HTMLElement>(order: readonly (string | number)[]) => {
  const containerRef = useRef<T>(null)
  const positions = useRef(new Map<string, number>())
  const previousOrder = useRef<string | null>(null)
  const orderKey = order.join(',')

  useLayoutEffect(() => {
    const container = containerRef.current
    if (!container) {
      return
    }
    const hasReordered = previousOrder.current !== null && previousOrder.current !== orderKey
    previousOrder.current = orderKey
    const shouldAnimate = hasReordered && !window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

    const rows = [...container.querySelectorAll<HTMLElement>('[data-reorder-key]')]
    const next = new Map<string, number>()
    rows.forEach(row => {
      const key = row.dataset.reorderKey ?? ''
      const top = row.offsetTop
      const before = positions.current.get(key)
      next.set(key, top)
      if (shouldAnimate && before !== undefined && before !== top && typeof row.animate === 'function') {
        row.animate([{ transform: `translateY(${before - top}px)` }, { transform: 'none' }], {
          duration,
          easing: 'ease-out',
        })
      }
    })
    positions.current = next
  })

  return containerRef
}
